import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api";
import LoaderBlock from "../loader/LoaderBlock";
import Pagination from "../shared/Pagination";
import {
    ABC_META,
    buildParams,
    downloadBlob,
    fmtCurrency,
    fmtNumber,
    STATUS_META,
} from "./analyticsUtils";

const STATUS_FILTERS = [
    { key: "all", label: "All" },
    { key: "reorder", label: "Reorder" },
    { key: "overstock", label: "Overstock" },
    { key: "dead_stock", label: "Dead stock" },
    { key: "ok", label: "Healthy" },
];

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.ok;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.cls}`}>
            <i className={`fa-solid ${meta.icon}`} />
            {meta.label}
        </span>
    );
};

const SummaryPill = ({ label, value, cls }) => (
    <div className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${cls}`}>
        {label}: {fmtNumber(value)}
    </div>
);

const ReorderTable = ({ paramsBase, periodLabel }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("reorder");
    const [exporting, setExporting] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildParams(paramsBase, { page, limit: 12, status });
            const { data: res } = await api.get(
                `/api/v1/analytics/reorder-recommendations?${params.toString()}`
            );
            if (res.success) setData(res);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to load recommendations");
        } finally {
            setLoading(false);
        }
    }, [paramsBase, page, status]);

    useEffect(() => {
        const timer = setTimeout(fetchData, 250);
        return () => clearTimeout(timer);
    }, [fetchData]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = buildParams(paramsBase);
            const response = await api.get(
                `/api/v1/analytics/export/purchase-order?${params.toString()}`,
                { responseType: "blob" }
            );
            const stamp = `${paramsBase.year}_${String(paramsBase.month).padStart(2, "0")}`;
            downloadBlob(response, `purchase_order_${stamp}.xlsx`);
            toast.success("Purchase order exported");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to export purchase order");
        } finally {
            setExporting(false);
        }
    };

    const summary = data?.summary || {};
    const rows = data?.rows || [];

    return (
        <div className="bg-white rounded-2xl shadow-1 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                    <h3 className="font-bold text-slate-800 text-[15px]">
                        <i className="fa-solid fa-clipboard-check text-[var(--color-2)] mr-2" />
                        Reorder Recommendations
                    </h3>
                    <p className="text-[12px] text-slate-400">
                        Safety stock &amp; reorder point from forecasted demand{periodLabel ? ` · ${periodLabel}` : ""}.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-[13px] font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                    <i className="fa-solid fa-file-excel" />
                    {exporting ? "Exporting…" : "Export Purchase Order"}
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
                <SummaryPill label="Reorder" value={summary.reorder || 0} cls="bg-red-50 text-red-700" />
                <SummaryPill label="Overstock" value={summary.overstock || 0} cls="bg-amber-50 text-amber-700" />
                <SummaryPill label="Dead stock" value={summary.dead_stock || 0} cls="bg-slate-100 text-slate-600" />
                <SummaryPill label="Healthy" value={summary.ok || 0} cls="bg-green-50 text-green-700" />
            </div>

            <div className="flex flex-wrap gap-1 mb-3">
                {STATUS_FILTERS.map((f) => (
                    <button
                        key={f.key}
                        onClick={() => {
                            setPage(1);
                            setStatus(f.key);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
                            status === f.key
                                ? "bg-[var(--color-2)] text-white"
                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <LoaderBlock />
            ) : rows.length === 0 ? (
                <p className="text-center text-slate-400 text-[13px] py-8">No items for this filter.</p>
            ) : (
                <div className="overflow-x-auto my-scroll">
                    <table className="w-full text-[13px]">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="th-text px-3 py-2 font-semibold">Product</th>
                                <th className="th-center px-3 py-2 font-semibold">Status</th>
                                <th className="th-num px-3 py-2 font-semibold">On Hand</th>
                                <th className="th-num px-3 py-2 font-semibold">Cover</th>
                                <th className="th-num px-3 py-2 font-semibold">Avg/mo</th>
                                <th className="th-num px-3 py-2 font-semibold">Safety</th>
                                <th className="th-num px-3 py-2 font-semibold">ROP</th>
                                <th className="th-num px-3 py-2 font-semibold">Order Qty</th>
                                <th className="th-num px-3 py-2 font-semibold">Est. Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.upc} className="border-b border-slate-50 hover:bg-slate-50">
                                    <td className="td-text px-3 py-2">
                                        <p className="font-semibold text-slate-700 truncate max-w-[220px]">
                                            {r.description}
                                        </p>
                                        <p className="text-[11px] text-slate-400">
                                            {r.upc}
                                            <span className={`ml-2 px-1.5 rounded-full text-[10px] font-bold ${ABC_META[r.abc_class]}`}>
                                                {r.abc_class}
                                            </span>
                                        </p>
                                    </td>
                                    <td className="td-center px-3 py-2"><StatusBadge status={r.status} /></td>
                                    <td className="td-num px-3 py-2 text-slate-600">{fmtNumber(r.on_hand)}</td>
                                    <td className="td-num px-3 py-2 text-slate-500">
                                        {r.days_of_cover === null ? "—" : `${r.days_of_cover}d`}
                                    </td>
                                    <td className="td-num px-3 py-2 text-slate-600">{r.avg_monthly_demand}</td>
                                    <td className="td-num px-3 py-2 text-slate-500">{fmtNumber(r.safety_stock)}</td>
                                    <td className="td-num px-3 py-2 text-slate-500">{fmtNumber(r.reorder_point)}</td>
                                    <td className="td-num px-3 py-2 font-bold text-[var(--color-2)]">
                                        {fmtNumber(r.suggested_order_qty)}
                                    </td>
                                    <td className="td-num px-3 py-2 text-slate-600">
                                        {fmtCurrency(r.suggested_order_qty * (r.each_cost || 0))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination page={page} totalPages={data?.totalPages || 1} setPage={setPage} />
        </div>
    );
};

export default ReorderTable;
